import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../utils/api";
import {
  FaDice,
  FaCalendarAlt,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaTimes,
  FaCopy,
  FaCheck,
  FaTrophy,
  FaArrowDown,
  FaCoins,
  FaChartLine,
} from "react-icons/fa";

const formatMoney = (value, currency = "INR") => {
  const symbol = currency === "INR" ? "₹" : currency + " ";
  return `${symbol}${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const BetHistory = () => {
  // Data State
  const [bets, setBets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter State
  const [filterResult, setFilterResult] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Pagination State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBets, setTotalBets] = useState(0);
  const [pageInput, setPageInput] = useState(1);

  // Copy feedback
  const [copiedId, setCopiedId] = useState(null);

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 800);
    return () => clearTimeout(timer);
  }, [search]);

  // Sync pageInput with page
  useEffect(() => {
    setPageInput(page);
  }, [page]);

  const formatDateLocal = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleDateRangeChange = (e) => {
    const range = e.target.value;
    setDateRange(range);
    setPage(1);

    const today = new Date();
    const todayStr = formatDateLocal(today);

    if (range === "all") {
      setDateFrom("");
      setDateTo("");
    } else if (range === "today") {
      setDateFrom(todayStr);
      setDateTo(todayStr);
    } else if (range === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      setDateFrom(formatDateLocal(yesterday));
      setDateTo(formatDateLocal(yesterday));
    } else if (range === "week") {
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      setDateFrom(formatDateLocal(lastWeek));
      setDateTo(todayStr);
    } else if (range === "month") {
      const lastMonth = new Date(today);
      lastMonth.setDate(today.getDate() - 30);
      setDateFrom(formatDateLocal(lastMonth));
      setDateTo(todayStr);
    }
  };

  // Fetch Bet History
  useEffect(() => {
    const fetchBets = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          limit,
          result: filterResult || undefined,
          startDate: dateFrom || undefined,
          endDate: dateTo || undefined,
          search: debouncedSearch || undefined,
        };

        const { data } = await api.get("/transactions/me/bets", { params });

        if (data.success) {
          setBets(data.bets);
          setSummary(data.summary);
          setTotalPages(data.pagination.pages);
          setTotalBets(data.pagination.total);
        }
      } catch (error) {
        console.error("Failed to fetch bet history", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBets();
  }, [page, limit, filterResult, dateFrom, dateTo, debouncedSearch]);

  // Page input handling
  const handlePageInput = (e) => {
    const val = e.target.value;
    if (val === "") {
      setPageInput("");
      return;
    }
    if (/^\d+$/.test(val)) {
      setPageInput(val);
    }
  };

  useEffect(() => {
    if (pageInput === "" || isNaN(parseInt(pageInput))) return;

    const timer = setTimeout(() => {
      const clampedVal = Math.max(1, Math.min(parseInt(pageInput), totalPages));
      if (clampedVal !== page) setPage(clampedVal);
      if (String(clampedVal) !== String(pageInput)) setPageInput(clampedVal);
    }, 800);

    return () => clearTimeout(timer);
  }, [pageInput, totalPages, page]);

  const copyRoundId = (roundId) => {
    navigator.clipboard?.writeText(roundId).then(() => {
      setCopiedId(roundId);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const resultBadge = (result) => {
    if (result === "win")
      return <span className="badge rounded-pill bg-success px-3">Win</span>;
    if (result === "refund")
      return (
        <span className="badge rounded-pill bg-warning text-dark px-3">
          Refund
        </span>
      );
    return <span className="badge rounded-pill bg-danger px-3">Loss</span>;
  };

  const netPositive = summary && summary.net >= 0;

  return (
    <div className="container mt-4 mb-5">
      {/* Summary Tiles */}
      <div className="row g-2 g-md-3 mb-3">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3 d-flex align-items-center gap-3">
              <div className="rounded-circle bg-primary bg-opacity-10 p-2 text-primary flex-shrink-0">
                <FaDice />
              </div>
              <div className="min-w-0">
                <div className="text-muted small">Total Rounds</div>
                <div className="fw-bold fs-6">
                  {summary ? summary.totalRounds.toLocaleString("en-IN") : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3 d-flex align-items-center gap-3">
              <div className="rounded-circle bg-success bg-opacity-10 p-2 text-success flex-shrink-0">
                <FaTrophy />
              </div>
              <div className="min-w-0">
                <div className="text-muted small">Total Won</div>
                <div className="fw-bold fs-6 text-success text-truncate">
                  {summary ? formatMoney(summary.totalWon) : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3 d-flex align-items-center gap-3">
              <div className="rounded-circle bg-danger bg-opacity-10 p-2 text-danger flex-shrink-0">
                <FaArrowDown />
              </div>
              <div className="min-w-0">
                <div className="text-muted small">Total Lost</div>
                <div className="fw-bold fs-6 text-danger text-truncate">
                  {summary ? formatMoney(summary.totalLost) : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body py-3 d-flex align-items-center gap-3">
              <div
                className={`rounded-circle p-2 flex-shrink-0 ${
                  netPositive
                    ? "bg-success bg-opacity-10 text-success"
                    : "bg-danger bg-opacity-10 text-danger"
                }`}
              >
                <FaChartLine />
              </div>
              <div className="min-w-0">
                <div className="text-muted small">Net P/L</div>
                <div
                  className={`fw-bold fs-6 text-truncate ${
                    netPositive ? "text-success" : "text-danger"
                  }`}
                >
                  {summary
                    ? `${netPositive ? "+" : "-"}${formatMoney(
                        Math.abs(summary.net)
                      )}`
                    : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 overflow-hidden">
        {/* Header: Title + Search + Filter Toggle */}
        <div className="card-header bg-white py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
          <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
            <FaDice className="text-primary" />
            Bet History
          </h5>

          <div className="d-flex gap-2 align-items-center">
            <div
              className="input-group input-group-sm search-group"
              style={{ width: "250px" }}
            >
              <span className="input-group-text text-muted bg-transparent border-0">
                <FaSearch />
              </span>
              <input
                type="text"
                className="form-control text-dark bg-transparent border-0 shadow-none ps-0"
                placeholder="Game or Round ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <span
                  className="input-group-text bg-transparent border-0 text-muted"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSearch("")}
                >
                  <FaTimes />
                </span>
              )}
            </div>
            <button
              className={`btn btn-sm d-flex align-items-center gap-2 ${
                showFilters ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setShowFilters(!showFilters)}
              style={{ height: "31px" }}
            >
              <FaFilter /> <span className="d-none d-sm-inline">Filters</span>
            </button>
          </div>
        </div>

        {/* Collapsible Filter Section */}
        {showFilters && (
          <div className="bg-light p-3 border-bottom">
            <div className="row g-3">
              {/* Result Filter */}
              <div className="col-lg-4 col-md-4 col-sm-6 col-12">
                <label className="form-label small text-muted fw-bold text-uppercase mb-1">
                  Result
                </label>
                <select
                  className="form-select bg-white text-dark"
                  value={filterResult}
                  onChange={(e) => {
                    setPage(1);
                    setFilterResult(e.target.value);
                  }}
                >
                  <option value="">All Results</option>
                  <option value="win">Win</option>
                  <option value="loss">Loss</option>
                  <option value="refund">Refund</option>
                </select>
              </div>

              {/* Time Period */}
              <div className="col-lg-4 col-md-4 col-sm-6 col-12">
                <label className="form-label small text-muted fw-bold text-uppercase mb-1">
                  Time Period
                </label>
                <select
                  className="form-select bg-white text-dark"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">Since Last Week</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Reset Button */}
              <div className="col-lg-4 col-md-4 col-sm-12 col-12 d-flex align-items-end">
                <button
                  className="btn btn-outline-danger w-100 bg-white"
                  onClick={() => {
                    setFilterResult("");
                    setDateRange("all");
                    setDateFrom("");
                    setDateTo("");
                    setSearch("");
                    setPage(1);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Custom Date Range */}
            {dateRange === "custom" && (
              <div className="row mt-3">
                <div className="col-lg-5 col-md-6 col-12 ms-lg-auto">
                  <div className="input-group input-group-sm">
                    <input
                      type="date"
                      className="form-control bg-white text-dark"
                      value={dateFrom}
                      onChange={(e) => {
                        setPage(1);
                        setDateFrom(e.target.value);
                      }}
                    />
                    <span className="input-group-text bg-light text-muted">
                      to
                    </span>
                    <input
                      type="date"
                      className="form-control bg-white text-dark"
                      value={dateTo}
                      onChange={(e) => {
                        setPage(1);
                        setDateTo(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2 text-muted">Loading bet history...</p>
            </div>
          ) : bets.length === 0 ? (
            <div className="text-center py-5">
              <div className="display-1 text-muted opacity-25 mb-3">
                <FaDice />
              </div>
              <p className="text-muted">No bets found.</p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {bets.map((bet) => {
                const isWin = bet.result === "win";
                const isRefund = bet.result === "refund";
                return (
                  <div
                    key={bet._id}
                    className="list-group-item p-3 hover-bg-light"
                    style={{
                      borderLeft: isWin
                        ? "4px solid #198754"
                        : isRefund
                        ? "4px solid #ffc107"
                        : "4px solid #dc3545",
                    }}
                  >
                    <div className="d-flex gap-3">
                      {/* Game Thumbnail / Icon */}
                      <div className="flex-shrink-0">
                        {bet.gameThumbnail ? (
                          <img
                            src={bet.gameThumbnail}
                            alt={bet.gameName}
                            className="rounded"
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div
                            className="rounded bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center"
                            style={{ width: 48, height: 48 }}
                          >
                            <FaCoins size={20} />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1 w-100">
                        {/* Game Name + Badge */}
                        <div className="d-flex justify-content-between align-items-start gap-2">
                          <div className="min-w-0">
                            <span className="fw-bold text-dark d-block text-truncate">
                              {bet.gameName}
                            </span>
                            {bet.providerName && (
                              <span className="text-muted small">
                                {bet.providerName}
                              </span>
                            )}
                          </div>
                          {resultBadge(bet.result)}
                        </div>

                        {/* Bet / Win / Refund / Net */}
                        <div className="d-flex flex-wrap gap-3 gap-sm-4 mt-2">
                          {!(isRefund && !bet.betAmount && !bet.winAmount) && (
                            <>
                              <div>
                                <div className="text-muted text-uppercase small">
                                  Bet
                                </div>
                                <div className="fw-bold text-dark">
                                  {formatMoney(bet.betAmount, bet.currency)}
                                </div>
                              </div>
                              <div>
                                <div className="text-muted text-uppercase small">
                                  Win
                                </div>
                                <div className="fw-bold text-dark">
                                  {formatMoney(bet.winAmount, bet.currency)}
                                </div>
                              </div>
                            </>
                          )}
                          {bet.refundAmount > 0 && (
                            <div>
                              <div className="text-muted text-uppercase small">
                                Refunded
                              </div>
                              <div className="fw-bold" style={{ color: "#b8860b" }}>
                                {formatMoney(bet.refundAmount, bet.currency)}
                              </div>
                            </div>
                          )}
                          <div>
                            <div className="text-muted text-uppercase small">
                              Net
                            </div>
                            <div
                              className={`fw-bold ${
                                bet.net >= 0 ? "text-success" : "text-danger"
                              }`}
                            >
                              {bet.net >= 0 ? "+" : "-"}
                              {formatMoney(Math.abs(bet.net), bet.currency)}
                            </div>
                          </div>
                          <div className="ms-sm-auto text-sm-end">
                            <div className="text-muted text-uppercase small">
                              Balance After
                            </div>
                            <div className="fw-medium text-muted">
                              {formatMoney(bet.balanceAfter, bet.currency)}
                            </div>
                          </div>
                        </div>

                        {/* Round ID + Date */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mt-2 gap-1">
                          <span
                            className="badge bg-light text-secondary border font-monospace text-truncate"
                            style={{ maxWidth: "100%", cursor: "pointer" }}
                            title="Click to copy Round ID"
                            onClick={() => copyRoundId(bet.roundId)}
                          >
                            Round: {bet.roundId}{" "}
                            {copiedId === bet.roundId ? (
                              <FaCheck className="text-success ms-1" />
                            ) : (
                              <FaCopy className="ms-1 opacity-50" />
                            )}
                          </span>

                          <span className="text-muted small flex-shrink-0">
                            <FaCalendarAlt className="me-1" size={12} />
                            {new Date(bet.createdAt).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!loading && bets.length > 0 && (
            <div className="p-3 bg-light border-top d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="text-muted small fw-bold">
                  Total: {totalBets} bets
                </div>
                <div className="d-flex align-items-center gap-2">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={limit}
                    onChange={(e) => {
                      setPage(1);
                      setLimit(parseInt(e.target.value));
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-muted small">per page</span>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-outline-secondary btn-sm border-0"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page <= 1}
                >
                  <FaChevronLeft />
                </button>

                <div className="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    className="form-control form-control-sm text-center fw-bold text-primary border-primary"
                    style={{ width: "60px" }}
                    value={pageInput}
                    onChange={handlePageInput}
                  />
                  <span className="text-muted fw-bold">/ {totalPages}</span>
                </div>

                <button
                  className="btn btn-outline-secondary btn-sm border-0"
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={page >= totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .search-group {
          border: 1px solid #ced4da;
          border-radius: 0.25rem;
          background-color: #fff;
          transition: all 0.2s;
        }
        .search-group:focus-within {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .search-group .input-group-text {
          background-color: transparent !important;
          border: none !important;
          color: #6c757d;
        }
        .search-group .form-control {
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        .search-group:focus-within .input-group-text {
          color: #0d6efd !important;
        }

        .hover-bg-light:hover { background-color: #f8f9fa; }
        .list-group-item { transition: background-color 0.2s; }
        .min-w-0 { min-width: 0; }

        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        .form-control, .form-select {
          background-color: #fff !important;
          color: #000 !important;
          border: 1px solid #ced4da;
        }
        .form-control:focus, .form-select:focus {
          background-color: #fff !important;
          color: #000 !important;
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
      `}</style>
    </div>
  );
};

export default BetHistory;
